import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Modal, Paper, FormHelperText, Tooltip, IconButton, withStyles } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { DatePicker, TimePicker } from '@material-ui/pickers'
import { Label, TextField, CleanButton } from '../default'
import { Loader, Alert, PrimaryButton, SelectField } from '../../components/default'
import { acGetAllValidNotifications, acPostNotification, acGetAllFilters } from '../../class/actions'

import { DB_CONST } from '../../class/constants'

function CreateNotificationModal({
  classes, open, onClose,
  createNotificationError, creatingNotification,
  loadingFilters, filters
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [notificationTypes, setNotificationTypes] = useState([])
  const [selectedType, setSelectedType] = useState()
  const [typeError, setTypeError] = useState(false)
  const [typeHelperText, setTypeHelperText] = useState(t('modals.notifications.create.type.helperText'))

  const [validUntil, setValidUntil] = useState(new Date())

  const [message, setMessage] = useState('')
  const [messageError, setMessageError] = useState(false)
  const [messageHelperText, setMessageHelperText] = useState(t('modals.notifications.create.message.helperText'))

  const [link, setLink] = useState('')
  const [linkHelperText] = useState(t('modals.notifications.create.link.helperText'))

  const [selectedFilter, setSelectedFilter] = useState()
  const [filterHelperText] = useState(t('modals.notifications.create.filters.helperText'))

  useEffect(() => {

    setNotificationTypes(DB_CONST.NOTIFICATION_TYPES_AUX.map(notificationType => {
      return {
        name: t(`modals.notifications.create.type.${notificationType.type}`),
        value: notificationType.id
      }
    }))

  }, [t])

  useEffect(() => {
    if (!filters)
      dispatch(acGetAllFilters())
  }, [dispatch, filters])

  useEffect(() => {

    if (selectedType !== '') {
      setTypeError(false)
      setTypeHelperText(t('modals.notifications.create.type.helperText'))
    }

    setLink('')

  }, [t, selectedType])

  useEffect(() => {

    if (message.length > 0) {
      setMessageError(false)
      setMessageHelperText(t('modals.notifications.create.message.helperText'))
    }

  }, [t, message])

  const setInicialState = () => {
    setValidUntil(new Date())
    setSelectedType()
    setMessage('')
    setLink('')
    setSelectedFilter()
    dispatch(acGetAllValidNotifications())
      .catch(err => console.error(err))
    onClose()
  }

  const createNotification = () => {

    if (!selectedType) {
      setTypeError(true)
      setTypeHelperText(t('modals.notifications.create.type.errorText'))
      return
    }

    if (message.length === 0) {
      setMessageError(true)
      setMessageHelperText(t('modals.notifications.create.message.errorText'))
      return
    }

    dispatch(acPostNotification(message, selectedType, validUntil.toISOString().replace('T', ' ').split('.')[0], link, selectedFilter))
      .then(() => {
        setInicialState()
      })
      .catch(err => console.error(err))

  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus style={{ outline: 0, overflowY: 'scroll' }}>
      <Paper
        square={true}
        className={classes.root + ' ' + classes.modalDiv}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Grid container spacing={2} className={classes.grid}>

            <Grid item xs={8}>
              <Label v='h6'>
                {t('modals.notifications.create.title')}
              </Label>
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
              <Tooltip title={t('default.close')}>
                <IconButton onClick={onClose}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={2}>
              <Label v='body2'>
                {t('modals.notifications.create.validUntil.label')}
              </Label>
            </Grid>

            <Grid item xs={4}>
              <DatePicker
                label={t('modals.notifications.create.validUntil.date')}
                format="dd/MM/yyyy"
                disablePast
                value={validUntil}
                onChange={e => setValidUntil(e)}
                showTodayButton
                todayLabel={t('modals.notifications.create.validUntil.today')}
                required
              />
            </Grid>

            <Grid item xs={4}>
              <TimePicker
                label={t('modals.notifications.create.validUntil.time')}
                ampm={false}
                value={validUntil}
                onChange={e => setValidUntil(e)}
                showTodayButton
                todayLabel={t('modals.notifications.create.validUntil.now')}
                minutesStep={5}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <SelectField
                label={t('modals.notifications.create.type.label')}
                items={notificationTypes}
                value={selectedType}
                onChange={e => setSelectedType(e.target.value)}
                error={typeError}
              />
              <FormHelperText error={typeError} style={{ marginLeft: 10 }}>{typeHelperText}</FormHelperText>
            </Grid>

            <Grid item xs={12}>
              <TextField
                light
                variant='filled'
                style={{ width: '100%', height: '100%' }}
                type='text'
                multiline
                rows={4}
                label={t('modals.notifications.create.message.label')}
                value={message}
                onChange={e => setMessage(e.target.value)}
                error={messageError}
                helperText={messageHelperText}
                required
              />
            </Grid>

            {
              selectedType === DB_CONST.NOTIFICATION_TYPES.INFO && (
                <Grid item xs={12}>
                  <TextField
                    light
                    variant='filled'
                    style={{ width: '100%', height: '100%' }}
                    type='text'
                    label={t('modals.notifications.create.link.label')}
                    value={link}
                    onChange={e => setLink(e.target.value)}
                    helperText={linkHelperText}
                  />
                </Grid>
              )
            }

            {
              (loadingFilters || !filters) ?
                <Loader /> : (
                  <Grid container justify='center'
                    item xs={12}
                  >
                    <Grid item xs={selectedFilter ? 11 : 12}>
                      <SelectField
                        label={t('modals.notifications.create.filters.label')}
                        items={filters.slice(1).map(filter => ({
                          name: filter.name,
                          value: filter.filter_id
                        }))}
                        value={selectedFilter}
                        onChange={e => setSelectedFilter(e.target.value)}
                      />
                      <FormHelperText style={{ marginLeft: 10 }}>{filterHelperText}</FormHelperText>
                    </Grid>
                    {
                      selectedFilter && (
                        <Grid item xs={1} style={{ textAlign: 'right', padding: 0 }}>
                          <Tooltip title={t('modals.notifications.create.filters.tooltip')}>
                            <IconButton onClick={() => setSelectedFilter()}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      )
                    }
                  </Grid>
                )
            }

          </Grid>
        </Box>

        <>
          <div>
            {
              createNotificationError && (
                <Alert style={{ marginTop: '15px' }} noMargin color="error" text={t('modals.notifications.create.error')} />
              )
            }
          </div>
          <div style={{ marginTop: '15px' }}>
            <Grid container style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CleanButton onClick={onClose} style={{ marginRight: '15px' }}>
                {t('default.cancel')}
              </CleanButton>
              <PrimaryButton onClick={createNotification}>
                {
                  creatingNotification ? (
                    <Loader />
                  ) : (
                    t('modals.notifications.create.confirmButton')
                  )
                }
              </PrimaryButton>
            </Grid>
          </div>
        </>

      </Paper>
    </Modal>
  )
}

CreateNotificationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  close: PropTypes.func
}

CreateNotificationModal.defaultProps = {
  open: false,
  onClose: () => true,
  close: () => true
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    padding: theme.spacing(2),
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  modalDiv: {
    [theme.breakpoints.up('md')]: {
      width: '35%'
    },
    width: '100%',
    top: '10%',
    left: '50%',
    transform: 'translate(-50%, -10%)',
  }
})

const mapStateToProps = ({ ReducerNotification, ReducerFilter }) => ({
  createNotificationError: ReducerNotification.createNotificationError,
  creatingNotification: ReducerNotification.creatingNotification,
  loadingFilters: ReducerFilter.loadingFilters,
  filters: ReducerFilter.filters
})

export default connect(mapStateToProps)(withStyles(styles)(CreateNotificationModal))